import {naturalSortByKey} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useEffect} from 'react';

import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeTestDefinitionArchiveUnarchiveEventMetadata,
  TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED_EVENT,
  TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED_EVENT,
} from 'performance/processSampleTestsEvents';
import {getCreateTestDefinitionURL, getTestDefinitionURL} from 'sample-tests/network/TestDefinitionDAO';
import TestDefinitionsListing from 'sample-tests/ui/test-definitions/TestDefinitionsListing';
import bufferForUX from 'util/bufferForUX';
import {withLoadingScreen} from 'util/Promises';

import {TestDefinitionsPageQuery} from './__generated/TestDefinitionsPageQuery';

type MaybeInaccessibleTestDefinition = TestDefinitionsPageQuery['allTestDefinitions'][number];

export type TestDefinitionForListing = ExtractByTypename<
  MaybeInaccessibleTestDefinition,
  'TestDefinitionNode'
>;

interface TestDefinitionsPageProps {
  testDefinitions: Array<TestDefinitionForListing> | null;
  errorMessage?: string;
  isWritable: boolean;
  refetch: () => void;
}

const TestDefinitionsPage = (props: TestDefinitionsPageProps): JSX.Element => {
  useEffect(() => {
    benchlingUtil.setWindowTitle('TestDefinitions');
  });

  return (
    <TestDefinitionsListing
      testDefinitions={props.testDefinitions}
      testDefinitionType="Test Definition"
      navigateToCreate={() => {
        benchlingUtil.navigate(null, getCreateTestDefinitionURL());
      }}
      isWritable={props.isWritable}
      urlForTestDefinition={getTestDefinitionURL}
      archiveTestDefinitions={async (
        testDefinitionIds: Array<string>,
        purpose: string,
        options: BulkArchiveFilesOptions
      ) => {
        logProfilerTimingEvent(TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED_EVENT);
        const bufferedPromise = async (): Promise<void> => {
          try {
            // bufferForUX is handling errors in a strange way, which causes duplicate error toasts
            // if we don't catch it here
            await bufferForUX(
              Promise.resolve(getArchiveAction(testDefinitionIds)(testDefinitionIds, purpose, options))
            );
          } catch (e) {
            if (e.status !== 400) {
              throw e;
            }
          }
        };
        await withLoadingScreen(bufferedPromise(), 'Archiving...');
        const metadata = composeTestDefinitionArchiveUnarchiveEventMetadata(true);
        logProfilerTimingEvent(TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED_EVENT, metadata);
        props.refetch();
      }}
      unarchiveTestDefinitions={async (testDefinitionIds: Array<string>) => {
        logProfilerTimingEvent(TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED_EVENT);
        const unarchiveAction = (): Promise<void> =>
          new Promise((resolve) => {
            resolve(getUnarchiveAction(testDefinitionIds)(testDefinitionIds));
          });
        await withLoadingScreen(bufferForUX(unarchiveAction()), 'Unarchiving...');
        const metadata = composeTestDefinitionArchiveUnarchiveEventMetadata(false);
        logProfilerTimingEvent(TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED_EVENT, metadata);
        props.refetch();
      }}
    />
  );
};

export const TEST_DEFINITIONS_PAGE_QUERY: DocumentNode = gql`
  query TestDefinitionsPageQuery @skipAudit @engComponent(name: SAMPLE_TESTS) {
    allTestDefinitions {
      ... on TestDefinitionNode {
        id
        name
        archiveData {
          archived
          purpose
        }
      }
    }
  }
`;

export default function ConnectedTestDefinitionsPage(): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;

  const queryResult = useBenchlingQuery<TestDefinitionsPageQuery>(TEST_DEFINITIONS_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const testDefinitions =
    queryResult.type === QueryResultType.SUCCESS
      ? naturalSortByKey(queryResult.data.allTestDefinitions, 'name')
      : null;

  return (
    <TestDefinitionsPage
      testDefinitions={testDefinitions}
      errorMessage={queryResult.type === QueryResultType.ERROR ? 'Error loading test definitions' : undefined}
      refetch={queryResult.refetch}
      isWritable={areOrganizationSchemasCreatable}
    />
  );
}
